import { Box, Divider, Drawer, IconButton } from '@mui/material';
import { useCart } from '@/hooks/useCart';
import { useEffect, useMemo, useState } from 'react';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import FlexBox from '@/atomic-components/FlexBox';
import CloseIcon from '@/assets/CloseIcon.svg';
import { getColor } from '@/utils/theme/colors';
import ReplaceIcon from '@/assets/actions/ReplaceIcon.svg';
import LencoButton from '@/atomic-components/LencoButton';
import { getStyle } from '@/utils/theme/fonts';
import { cartService } from '@/services/cart';
import { ApiState, BaseAxiosApiError } from '@/models/generics';
import { useLencoDispatch } from '@/hooks/useLencoDispatch';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { VerticalBasedOosCartItemsList } from './VerticalBasedOosCartItemsList';
import { CatalogueService } from '@/services/catalogue';
import { IProductData } from '@/components/search-results/model';
import { getCartContext } from '@/utils/cart';
import { useRouter } from 'next/router';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import { useLocation } from '@/hooks/useLocation';

type OOSCartItemsBulkActionDrawerProps = {
	open: boolean;
	onClose: () => void;
};

export const OOSCartItemsBulkActionDrawer = (
	props: OOSCartItemsBulkActionDrawerProps
) => {
	const [removingItemsApiState, setRemovingItemsApiState] = useState<ApiState>(
		ApiState.IDLE
	);
	const [moveItemsToWishlistApiState, setMoveItemsToWishlistApiState] =
		useState<ApiState>(ApiState.IDLE);
	const [relatedProducts, setRelatedProducts] =
		useState<Record<string, IProductData[]>>();
	const {
		buyCartItems,
		rentalCartItems,
		totalCartItems,
		getCartApiState,
		cartActions,
	} = useCart();
	const dispatch = useLencoDispatch();
	const { loader } = useFullScreenLoader();
	const router = useRouter();
	const {
		actions: { notifyUser },
	} = useNotification();
	const { selectedLocation } = useLocation();

	const rentalOosItems = useMemo(() => {
		return rentalCartItems?.filter((item) => item.isOutOfStock);
	}, [rentalCartItems]);

	const buyOosItems = useMemo(() => {
		return buyCartItems?.filter((item) => item.isOutOfStock);
	}, [buyCartItems]);

	const getRelatedProductsForOosItems = () => {
		const permalinks = getAllOosItemsPermalink();

		if (permalinks.length == 0) return;

		CatalogueService.getBulkRelatedProduct({
			permalinks: permalinks,
		}).then((res) => {
			setRelatedProducts(res);
		});
	};

	const removeAllOosItemsFromCart = () => {
		setRemovingItemsApiState(ApiState.LOADING);
		loader.show();

		cartService
			.bulkRemoveCartItems({
				cartItems: [],
				shouldRemoveAllOOSItems: true,
				context: getCartContext(router),
			})
			.then((res) => {
				notifyUser({
					message: res.data.data.message,
					severity: NotificationSeverity.SUCCESS,
				});
				cartActions.getCart();
				setRemovingItemsApiState(ApiState.IDLE);
			})
			.catch((e) => {
				const error = e as BaseAxiosApiError;
				notifyUser({
					message: error.response?.data.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				loader.hide();
			});
	};

	const getAllOosItemsPermalink = (): string[] => {
		const permalinks: string[] = [];

		rentalOosItems.forEach((item) => permalinks.push(item.product.permalink));
		buyOosItems.forEach((item) => permalinks.push(item.product.permalink));

		return permalinks;
	};

	const getAllOosItemsCatalogueIds = (): number[] => {
		const ids: number[] = [];

		rentalOosItems.forEach((item) => ids.push(item.product.id));
		buyOosItems.forEach((item) => ids.push(item.product.id));

		return ids;
	};

	const moveAllToWishlist = () => {
		setMoveItemsToWishlistApiState(ApiState.LOADING);
		loader.show();

		cartService
			.bulkMoveCartItemsToWishlist({
				shouldRemoveAllOOSItems: true,
				context: getCartContext(router),
				catalogIds: getAllOosItemsCatalogueIds(),
			})
			.then((res) => {
				notifyUser({
					message: res.data.data.message,
					severity: NotificationSeverity.SUCCESS,
				});
				cartActions.getCart();
				setMoveItemsToWishlistApiState(ApiState.IDLE);
			})
			.catch((e) => {
				const error = e as BaseAxiosApiError;
				notifyUser({
					message: error.response?.data.error?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				loader.hide();
			});
	};

	useEffect(() => {
		if (getCartApiState == ApiState.SUCCESS) {
			getRelatedProductsForOosItems();
		}
	}, [getCartApiState]);

	return (
		<Drawer anchor='right' open={props.open} onClose={props.onClose}>
			<Box width='464px' p='0px 40px' pt='80px' mb='80px'>
				<FlexBox justifyContent='space-between' alignItems='center'>
					<LencoTypography variant='H11_Medium'>
						Out of stock items in Cart
					</LencoTypography>
					<IconButton onClick={props.onClose}>
						<CloseIcon />
					</IconButton>
				</FlexBox>
				<Divider sx={{ m: '24px 0px' }} />
				<LencoTypography variant='P_Regular' color={getColor('Gray -1')}>
					Please remove{' '}
					<span style={getStyle('P_Medium')} color={getColor('Gray 0')}>
						Out of Stock
					</span>{' '}
					items from your cart to checkout, or you can also:
				</LencoTypography>
				<FlexBox
					className='cursor-default'
					sx={{
						background: getColor('Minty -6'),
						p: '8px 16px',
						borderRadius: '8px',
						border: `solid 1px ${getColor('Minty -4')}`,
					}}
					mt='16px'
					mb='24px'
				>
					<ReplaceIcon />
					<LencoTypography ml='8px' color={getColor('Gray -1')}>
						Replace the product with a similar item
					</LencoTypography>
				</FlexBox>
				{rentalOosItems.length > 0 && (
					<VerticalBasedOosCartItemsList
						title='Rent Cart'
						items={rentalOosItems}
						relatedProducts={relatedProducts}
					/>
				)}
				{buyOosItems.length > 0 && (
					<VerticalBasedOosCartItemsList
						title='Buy Cart'
						items={buyOosItems}
						relatedProducts={relatedProducts}
					/>
				)}
			</Box>
			<Box
				sx={{
					width: '464px',
					p: '0px 40px',
					position: 'fixed',
					bottom: '0px',
					zIndex: 2,
					background: 'white',
				}}
			>
				<Divider />
				<FlexBox
					justifyContent='space-between'
					alignItems='center'
					m='24px 0px'
				>
					<LencoButton
						variant='contained'
						color='secondary'
						onClick={removeAllOosItemsFromCart}
						isLoading={removingItemsApiState == ApiState.LOADING}
						sx={{ width: '162px', ...getStyle('Small_Medium') }}
					>
						Remove All
					</LencoButton>
					<LencoButton
						variant='contained'
						color='primary'
						onClick={moveAllToWishlist}
						isLoading={moveItemsToWishlistApiState == ApiState.LOADING}
						sx={{ width: '162px', ...getStyle('Small_Medium') }}
					>
						Move to wishlist
					</LencoButton>
				</FlexBox>
			</Box>
		</Drawer>
	);
};
